import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Skeleton,
  Autocomplete,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AlertMessagesContext } from 'react-alert-messages';
import styles from './styles.module.css';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import PurchaseService from 'services/PurchaseService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { Link, useHistory, useParams } from 'react-router-dom';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ERROR_CODES, ITEM_TYPES, ROUTES, SUBSCRIPTION_TYPE } from 'const';
import EditIcon from '@mui/icons-material/Edit';
import DataTable from 'components/utils/DataTable';
import VendorServices from 'services/VendorServices';
import AccountServices from 'services/AccountServices';
import { VendorContext } from 'Context/VendorContext';
import CustomMenuItem from 'components/utils/CustomComponents/CustomMenuItem';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import NewVendorDialog from 'components/popups/NewVendorDialog';
import ConfirmationDialog from 'components/popups/ConfirmationDialog';
import AddItemDialog from 'components/popups/AddItemDialog';
import ItemDialog from 'components/popups/ItemDialog';
import MarginEditDialog from 'components/popups/MarginEditDialog';
import NewItemsTableDialog from 'components/popups/NewItemsTableDialog';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import { ShopContext } from 'Context/ShopContext';
import { InventoryContext } from 'Context/InventoryContext';
import ShopsHelper from 'helpers/ShopsHelper';
import { roundOff } from 'helpers/NumberFormatHelper';
import {
  calculatePriceFromMargin,
  getItemSellingPrice,
  getLineTotal,
} from 'helpers/PurchaseHelper';
import NumberField from 'components/library/NumberField';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import BarcodeHelper from 'helpers/BarcodeHelper';
import useBarcodeScanner from 'hooks/useBarcodeScanner';
import { WarehouseContext } from 'Context/WarehouseContext';

function AddPurchase() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { products: items, getProducts, setProducts, getStockBatches } = useContext(InventoryContext);
  const { warehouses, getWarehouses, getWarehouseProducts } = useContext(WarehouseContext);

  const { id } = useParams();
  const history = useHistory();
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [editData, setEditData] = useState({});
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [item, setItem] = useState(null);
  const [billDate, setBillDate] = useState(new Date());
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [itemPlu, setItemPlu] = useState('');
  const [itemName, setItemName] = useState('');
  const [cost, setCost] = useState(0);
  const [price, setPrice] = useState(0);
  const [profitMargin, setProfitMargin] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [tax, setTax] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const { vendors, getVendors, setVendors } = useContext(VendorContext);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(Boolean(id));
  const [isShowAddVendonDialog, setShowAddVendonDialog] = useState(false);
  const [newItemsQueue, setNewItemsQueue] = useState([]);
  const [activeItemForDialog, setActiveItemForDialog] = useState(null);
  const [createdRowIndices, setCreatedRowIndices] = useState([]);
  const [vendorNameOld, setVendorNameOld] = useState('');
  const [stock, setStock] = useState(0);
  const [expiryDate, setExpiryDate] = useState(null);
  const [prevStock, setPrevStock] = useState(0);
  const [vendor, setVendor] = useState(null);
  const [extractedVendorName, setExtractedVendorName] = useState('');
  const [extractedVendorMobile, setExtractedVendorMobile] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [searchText, setSearchText] = useState('');
  const [batchName, setBatchName] = useState('');
  const [editPurchaseItemId, setEditPurchaseItemId] = useState(null);
  const [isShowForceUpdateDialog, setShowForceUpdateDialog] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [isAiParsing, setIsAiParsing] = useState(false);
  const [aiParseError, setAiParseError] = useState(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const [isShowMarginEditDialog, setIsShowMarginEditDialog] = useState(false);
  const [isSetBatchPrice, setIsSetBatchPrice] = useState(false);
  const [columnFilterVersion, setColumnFilterVersion] = useState(0);
  const [isShowAddItemDialog, setShowAddItemDialog] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemBarcode, setNewItemBarcode] = useState('');

  const isValidMargin = (val) => val !== undefined && val !== null && val !== '';

  const showProfitMarginAndPrice = ShopsHelper.isShowProfitMarginAndPriceInPurchase(shop);

  const PURCHASE_TABLE_COLUMNS_KEY = 'purchase_items_table_columns';

  const optionalColumns = useMemo(
    () => ({
      plu: true,
      ...(showProfitMarginAndPrice ? { price: true } : {}),
      stock: true,
      tax_percentage: true,
      tax: true,
      expiry_date: false,
    }),
    [showProfitMarginAndPrice]
  );

  useEffect(() => {
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, arguments);
      if (key === PURCHASE_TABLE_COLUMNS_KEY) {
        setColumnFilterVersion((v) => v + 1);
      }
    };
    return () => {
      localStorage.setItem = originalSetItem;
    };
  }, []);

  const isColumnVisible = useCallback(
    (colId) => {
      try {
        const filterDataStr = localStorage.getItem(PURCHASE_TABLE_COLUMNS_KEY);
        if (filterDataStr) {
          const filterData = JSON.parse(filterDataStr);
          if (filterData.hasOwnProperty(colId)) {
            return Boolean(filterData[colId]);
          }
        }
      } catch (e) {}
      return Boolean(optionalColumns[colId]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionalColumns, columnFilterVersion]
  );

  const calculateExpiryDate = useCallback(
    (selectedItem, baseDate = purchaseDate) => {
      const isBatch = Boolean(selectedItem?.config?.is_batch_enabled ?? selectedItem?.is_batch_enabled);
      const shelfHours = Number(selectedItem?.shelf_duration_in_hours);
      if (isBatch && !isNaN(shelfHours) && shelfHours > 0) {
        const start = baseDate ? new Date(baseDate) : new Date();
        return new Date(start.getTime() + shelfHours * 60 * 60 * 1000);
      }
      return null;
    },
    [purchaseDate]
  );

  const isItemAlreadyInDb = useMemo(() => {
    if (item && item.id) return true;
    if (!itemName?.trim()) return false;
    const norm = String(itemName).toLowerCase().trim();
    return (items || []).some(
      (p) =>
        (p.name || '').toLowerCase().trim() === norm ||
        (p.plu && String(p.plu).trim() === norm) ||
        (p.ean && String(p.ean).trim() === norm)
    );
  }, [item, itemName, items]);

  const getLevenshteinDistance = (str1, str2) => {
    const m = str1.length;
    const n = str2.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 0; i <= m; i += 1) dp[i][0] = i;
    for (let j = 0; j <= n; j += 1) dp[0][j] = j;
    for (let i = 1; i <= m; i += 1) {
      for (let j = 1; j <= n; j += 1) {
        if (str1[i - 1] === str2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
      }
    }
    return dp[m][n];
  };

  const compressImageInBrowser = (file) => {
    return new Promise((resolve) => {
      if (!file || !file.type?.startsWith('image/') || file.size < 400 * 1024) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const maxDim = 1600;
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            0.8
          );
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handleAiBillUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAiParseError(null);

    // Standard file size limit: 10 MB
    const MAX_FILE_SIZE_MB = 10;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const msg = `Selected file (${fileSizeMB} MB) exceeds the standard ${MAX_FILE_SIZE_MB} MB size limit. Please upload an image or PDF under ${MAX_FILE_SIZE_MB} MB.`;
      setAiParseError(msg);
      postAlertMessage({ text: msg, type: 'failed', timeout: 8000 });
      event.target.value = '';
      return;
    }

    if (!shop?.id) {
      const msg = 'Shop ID not found.';
      setAiParseError(msg);
      postAlertMessage({ text: msg, type: 'failed', timeout: 8000 });
      return;
    }

    try {
      setIsAiParsing(true);
      const fileToUpload = await compressImageInBrowser(file);
      const formData = new FormData();
      formData.append('documents', fileToUpload);

      const response = await PurchaseService.parsePurchaseInvoice(shop.id, formData);
      const billData = Array.isArray(response) ? response[0] : response;

      if (!billData || Object.keys(billData).length === 0) {
        const msg = 'Could not extract bill details from image.';
        setAiParseError(msg);
        postAlertMessage({ text: msg, type: 'failed', timeout: 8000 });
        return;
      }

      if (billData?.status && Number(billData.status) !== 200) {
        const rawErr = billData.message || billData.error || 'Could not extract bill details from image.';
        const errorMsg = typeof rawErr === 'object' ? JSON.stringify(rawErr) : String(rawErr);
        setAiParseError(errorMsg);
        postAlertMessage({ text: errorMsg, type: 'failed', timeout: 8000 });
        return;
      }

      if (billData.invoice_no) {
        setInvoiceNumber(billData.invoice_no);
      }

      if (billData.bill_date) {
        const parsedDate = new Date(billData.bill_date);
        if (!isNaN(parsedDate.getTime())) {
          setBillDate(parsedDate);
        }
      }

      const extractedMobile = (
        billData.vendor_mobile ||
        billData.mobile ||
        billData.vendor_phone ||
        billData.phone ||
        ''
      ).trim();

      if (billData.vendor_name || extractedMobile) {
        const extractedName = (billData.vendor_name || '').toLowerCase().trim();
        const cleanMobileDigits = extractedMobile.replace(/\D/g, '');

        const matchedVendor = (vendors || []).find((v) => {
          const vName = (v.name || '').toLowerCase().trim();
          const vMobileDigits = (v.mobile || '').replace(/\D/g, '');

          if (
            cleanMobileDigits &&
            vMobileDigits &&
            cleanMobileDigits.length >= 8 &&
            vMobileDigits.endsWith(cleanMobileDigits.slice(-10))
          ) {
            return true;
          }
          if (vName && extractedName) {
            if (vName === extractedName) return true;
            const maxLen = Math.max(vName.length, extractedName.length);
            if (maxLen >= 4) {
              const dist = getLevenshteinDistance(vName, extractedName);
              const similarity = 1 - dist / maxLen;
              if (similarity >= 0.85 && dist <= 3) return true;
            }
          }
          return false;
        });

        if (matchedVendor) {
          setVendor(matchedVendor);
          setVendorNameOld('');
          setExtractedVendorName('');
          setExtractedVendorMobile('');
        } else {
          setVendor(null);
          setVendorNameOld('');
          setExtractedVendorName(billData.vendor_name || '');
          setExtractedVendorMobile(extractedMobile);
          setShowAddVendonDialog(true);
        }
      } else {
        setVendor(null);
        setVendorNameOld('');
        setExtractedVendorName('');
        setExtractedVendorMobile('');
      }

      const extractedItems = billData.items || [];
      if (extractedItems.length > 0) {
        const newPurchasedItems = [];
        let maxExistingPlu = (items || []).reduce((max, i) => {
          if (!i || i.plu == null) return max;
          const strPlu = String(i.plu).trim();
          if (/^\d+$/.test(strPlu)) {
            const val = Number(strPlu);
            return !isNaN(val) ? Math.max(max, val) : max;
          }
          return max;
        }, 0);

        for (const extItem of extractedItems) {
          const extName = (extItem.name || '').toLowerCase().trim();
          const extPlu = extItem.plu ? String(extItem.plu).trim() : '';
          const extEan = extItem.ean || extItem.barcode ? String(extItem.ean || extItem.barcode).trim() : '';

          const matchedItem = (items || []).find((i) => {
            const iName = (i.name || '').toLowerCase().trim();
            const iPlu = i.plu ? String(i.plu).trim() : '';
            const iEan = i.ean ? String(i.ean).trim() : '';

            if (extName && iName && extName === iName) return true;
            if (extEan && iEan && extEan === iEan && (!extName || extName === iName)) return true;
            if (extPlu && iPlu && extPlu === iPlu && (!extName || extName === iName)) return true;

            return false;
          });

          const qty = Number(extItem.quantity) || 1;
          const listRate = Math.round((Number(extItem.rate) || 0) * 100) / 100;
          const discPct =
            Math.round(
              (Number(extItem.discount || extItem.disc || extItem.discount_percent || extItem.disc_pct) || 0) * 100
            ) / 100;

          let effectiveCostRate = listRate;
          if (discPct > 0 && discPct <= 100) {
            effectiveCostRate = Math.round(listRate * (1 - discPct / 100) * 100) / 100;
          } else if (discPct > 100 && listRate > 0) {
            effectiveCostRate = Math.round(Math.max(0, listRate - discPct / qty) * 100) / 100;
          }

          const baseTotal = Math.round(qty * effectiveCostRate * 100) / 100;

          let rawTax = Number(extItem.tax_percentage || extItem.tax_percent || extItem.tax) || 0;
          let taxPct = 0;
          let taxAmt = 0;

          if (rawTax > 0 && rawTax <= 100) {
            taxPct = Math.round(rawTax * 100) / 100;
            taxAmt = Math.round(((baseTotal * taxPct) / 100) * 100) / 100;
          } else if (rawTax > 100 && baseTotal > 0) {
            taxAmt = Math.round(rawTax * 100) / 100;
            taxPct = Math.round((taxAmt / baseTotal) * 100 * 100) / 100;
          } else if (extItem.total && Number(extItem.total) > baseTotal && baseTotal > 0) {
            taxAmt = Math.round((Number(extItem.total) - baseTotal) * 100) / 100;
            taxPct = Math.round((taxAmt / baseTotal) * 100 * 100) / 100;
          }

          const rawPayable = extItem.total ? Number(extItem.total) : baseTotal + taxAmt;
          const payableTotal = Math.round(rawPayable * 100) / 100;

          if (matchedItem) {
            const marginVal = matchedItem.config?.profit_margin || 0;
            const calculatedPrice =
              effectiveCostRate > 0 && marginVal > 0
                ? calculatePriceFromMargin(effectiveCostRate, marginVal)
                : matchedItem.price || 0;

            newPurchasedItems.push({
              plu: matchedItem.plu,
              item: {
                ...matchedItem,
                price: calculatedPrice,
              },
              rate: effectiveCostRate,
              listRate: listRate,
              discount: discPct,
              price: calculatedPrice,
              profit_margin: marginVal,
              quantity: qty,
              tax: taxAmt,
              payable: payableTotal,
              stock: (matchedItem.stock || 0) + qty,
              taxPercentage: taxPct,
              new_item: false,
              item_id: matchedItem.id,
            });
          } else {
            maxExistingPlu += 1;
            const autoPlu = maxExistingPlu;
            newPurchasedItems.push({
              plu: autoPlu,
              item: {
                name: extItem.name || 'New Item',
                price: effectiveCostRate,
                config: { profit_margin: 0 },
                plu: autoPlu,
              },
              rate: effectiveCostRate,
              listRate: listRate,
              discount: discPct,
              price: effectiveCostRate,
              profit_margin: 0,
              quantity: qty,
              tax: taxAmt,
              payable: payableTotal,
              stock: qty,
              taxPercentage: taxPct,
              new_item: true,
            });
          }
        }

        const matchedDbItems = [];
        const onlyNewItems = [];

        for (const pItem of newPurchasedItems) {
          if (!pItem.new_item) {
            matchedDbItems.push(pItem);
          } else {
            const itemNameNorm = (pItem.item?.name || '').toLowerCase().trim();
            const itemPluNorm = String(pItem.plu || '').trim();

            const existingInDb = (items || []).find((dbItem) => {
              const dbName = (dbItem.name || '').toLowerCase().trim();
              const dbPlu = String(dbItem.plu || '').trim();
              if (itemNameNorm && dbName && itemNameNorm === dbName) return true;
              if (itemPluNorm && dbPlu && itemPluNorm === dbPlu && (!itemNameNorm || itemNameNorm === dbName))
                return true;
              return false;
            });

            if (existingInDb) {
              const costRate = pItem.rate || existingInDb.cost || 0;
              const marginVal = existingInDb.config?.profit_margin || 0;
              const calculatedPrice =
                costRate > 0 && marginVal > 0
                  ? calculatePriceFromMargin(costRate, marginVal)
                  : existingInDb.price || pItem.price;

              matchedDbItems.push({
                plu: existingInDb.plu || pItem.plu,
                item: { ...existingInDb, price: calculatedPrice },
                item_id: existingInDb.id,
                rate: costRate,
                price: calculatedPrice,
                profit_margin: marginVal,
                quantity: pItem.quantity,
                stock: (existingInDb.stock || 0) + pItem.quantity,
                payable: pItem.payable,
                tax: pItem.tax || 0,
                taxPercentage: pItem.taxPercentage || 0,
                new_item: false,
              });
            } else {
              onlyNewItems.push(pItem);
            }
          }
        }

        if (matchedDbItems.length > 0) {
          setPurchasedItems((prev) => [...prev, ...matchedDbItems]);
        }

        postAlertMessage({
          text: `Parsed ${extractedItems.length} items from purchase bill with AI!`,
          type: 'success',
        });

        if (onlyNewItems.length > 0) {
          setNewItemsQueue(onlyNewItems);
        }
      } else {
        const rawErr = billData?.message || 'Bill parsed, but no items detected.';
        let errorMsg = 'Bill parsed, but no items detected.';
        if (typeof rawErr === 'string') errorMsg = rawErr;
        else if (typeof rawErr === 'object' && rawErr !== null) {
          const parts = [];
          Object.keys(rawErr).forEach((key) => {
            const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
            const val = rawErr[key];
            if (Array.isArray(val)) parts.push(`${label}: ${val.join(', ')}`);
            else if (typeof val === 'string') parts.push(`${label}: ${val}`);
          });
          errorMsg = parts.length > 0 ? parts.join(' | ') : String(rawErr);
        }
        setAiParseError(null);
        postAlertMessage({ text: errorMsg, type: 'info', timeout: 8000 });
      }
    } catch (err) {
      console.error('AI Bill parse error:', err);
      let resData = err?.response?.data || err?.data || err;
      if (resData && typeof resData === 'object' && resData.message) {
        resData = resData.message;
      }
      if (typeof resData === 'string') {
        try {
          if (resData.trim().startsWith('{') || resData.trim().startsWith('[')) {
            resData = JSON.parse(resData);
          }
        } catch (e) {
          // Keep string if not JSON
        }
      }

      let alertText = 'Failed to parse purchase bill.';
      if (typeof resData === 'string') alertText = resData;
      else if (typeof resData === 'object' && resData !== null) {
        if (resData.detail) alertText = String(resData.detail);
        else if (resData.message) alertText = String(resData.message);
        else {
          const parts = [];
          Object.keys(resData).forEach((key) => {
            if (['stack', 'config', 'headers', 'request'].includes(key)) return;
            const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
            const val = resData[key];
            if (Array.isArray(val)) parts.push(`${label}: ${val.join(', ')}`);
            else if (typeof val === 'string') parts.push(`${label}: ${val}`);
          });
          alertText = parts.length > 0 ? parts.join(' | ') : err.message || alertText;
        }
      }
      setAiParseError(alertText);
      postAlertMessage({ text: alertText, type: 'failed', timeout: 8000 });
    } finally {
      setIsAiParsing(false);
      event.target.value = '';
    }
  };

  const handleCreateNewItems = async (createdItemsMap, editedRows) => {
    try {
      const latestProducts = await getProducts();
      const newlyCreatedPurchaseItems = [];

      for (const row of editedRows || []) {
        const queueKey = (row.originalQueueItem?.item?.name || row.name).toLowerCase().trim();
        const createdObj = createdItemsMap[queueKey];
        const matchedProduct = (latestProducts || []).find(
          (p) => (p.name || '').toLowerCase().trim() === queueKey || (createdObj && p.id === createdObj.id)
        );

        if (matchedProduct || createdObj) {
          const itemObj = matchedProduct || createdObj;
          const finalRate = Number(row.rate) || 0;
          const finalPrice = Number(row.price) || finalRate;
          const finalMargin = Number(row.profit_margin) || 0;
          const finalStock = Number(row.stock) || 1;

          newlyCreatedPurchaseItems.push({
            plu: itemObj.plu || row.plu,
            item: {
              ...itemObj,
              price: finalPrice,
            },
            item_id: itemObj.id,
            rate: finalRate,
            price: finalPrice,
            profit_margin: finalMargin,
            quantity: finalStock,
            stock: (itemObj.stock || 0) + finalStock,
            payable: finalRate * finalStock,
            tax: 0,
            taxPercentage: 0,
            new_item: false,
          });
        }
      }

      if (newlyCreatedPurchaseItems.length > 0) {
        setPurchasedItems((prev) => [...prev, ...newlyCreatedPurchaseItems]);
      }
    } catch (err) {
      console.error('Error post-processing created items:', err);
    } finally {
      setNewItemsQueue([]);
    }
  };

  const handleDoneNewItems = () => {
    setNewItemsQueue([]);
  };

  const handleSkipNewItems = () => {
    setNewItemsQueue([]);
  };

  const handleCancelNewItems = () => {
    setNewItemsQueue([]);
  };

  const handleOpenItemDialogForCreate = (rowItem, index) => {
    setActiveItemForDialog({ rowItem, index });
  };

  const handleOpenDirectCreateItemDialog = () => {
    openCreateItemDialog(itemName.trim());
  };

  const displayedMargin = useMemo(() => {
  const num = Number(profitMargin);
  return profitMargin === '' || profitMargin === null || Number.isNaN(num)
    ? 0
    : roundOff(num, 2);
}, [profitMargin]);

const handleOpenProductMargin = useCallback(() => {
  if (!showProfitMarginAndPrice) return;
  setIsShowMarginEditDialog(true);
}, [showProfitMarginAndPrice]);

const suggestedPrice = useMemo(() => {
  const costVal = parseFloat(cost);
  if (Number.isNaN(costVal) || costVal <= 0) return null;

  const hasValidProfitMargin =
    profitMargin !== '' && profitMargin !== null && !Number.isNaN(Number(profitMargin));

  const marginVal = hasValidProfitMargin
    ? parseFloat(profitMargin)
    : parseFloat(item?.config?.profit_margin);

  if (Number.isNaN(marginVal) || marginVal <= 0) return null;

  const calculated = calculatePriceFromMargin(costVal, marginVal);
  return calculated !== null ? ShopsHelper.getAmountFormatted(shop, calculated) : null;
}, [cost, profitMargin, item, shop]);



  const handleItemDialogClose = (createdProductFromDialog) => {
    if (activeItemForDialog) {
      const { rowItem, index } = activeItemForDialog;
      try {
        const queueKey = (rowItem.name || '').toLowerCase().trim();
        const targetPlu = String(rowItem.plu || '');

        let createdItem = createdProductFromDialog && createdProductFromDialog.id ? createdProductFromDialog : null;

        if (!createdItem && items && Array.isArray(items)) {
          createdItem =
            items.find((p) => (p.name || '').toLowerCase().trim() === queueKey) ||
            items.find(
              (p) =>
                targetPlu &&
                String(p.plu) === targetPlu &&
                (!queueKey || (p.name || '').toLowerCase().trim() === queueKey)
            );
        }

        if (createdItem && createdItem.id && index >= 0) {
          const finalRate = Number(rowItem.rate) || Number(createdItem.cost) || 0;
          const finalPrice = Number(rowItem.price) || Number(createdItem.price) || finalRate;
          const finalMargin = Number(rowItem.profit_margin) || 0;
          const finalStock = Number(rowItem.stock) || 1;

          const newItemEntry = {
            plu: createdItem.plu || rowItem.plu,
            item: {
              ...createdItem,
              price: finalPrice,
            },
            item_id: createdItem.id,
            rate: finalRate,
            price: finalPrice,
            profit_margin: finalMargin,
            quantity: finalStock,
            stock: (createdItem.stock || 0) + finalStock,
            payable: finalRate * finalStock,
            tax: 0,
            taxPercentage: 0,
            new_item: false,
          };

          setPurchasedItems((prev) => {
            const existingIdx = prev.findIndex(
              (pi) => pi.item_id === createdItem.id || (createdItem.plu && String(pi.plu) === String(createdItem.plu))
            );
            if (existingIdx !== -1) {
              const updated = [...prev];
              updated[existingIdx] = newItemEntry;
              return updated;
            }
            return [...prev, newItemEntry];
          });

          setCreatedRowIndices((prev) => (prev.includes(index) ? prev : [...prev, index]));

          if (postAlertMessage) {
            postAlertMessage({
              text: `Successfully created "${createdItem.name}" and added to purchase list!`,
              type: 'success',
            });
          }
        }
      } catch (err) {
        console.error('Error post-processing created item:', err);
      } finally {
        setActiveItemForDialog(null);
      }
    } else {
      setActiveItemForDialog(null);
    }
  };

  const vendorInputRef = useRef(null);
  const invoiceInputRef = useRef(null);
  const pluInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const costInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const qtyInputRef = useRef(null);
  const taxPercentInputRef = useRef(null);
  const taxAmountInputRef = useRef(null);
  const totalInputRef = useRef(null);
  const expDateInputRef = useRef(null);
  const paymentInputRef = useRef(null);
  const isResettingRef = useRef(false);

  const filteredItems = useMemo(
    () => (items || []).filter((item) => item && (item.type === 'INVENTORY' || item.type === 'SERVICE' || !item.type)),
    [items]
  );

  useEffect(() => {
    if (vendorInputRef.current) {
      vendorInputRef.current.focus();
    } else if (pluInputRef.current) {
      pluInputRef.current.focus();
    } else if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const featureValidity = shop?.customer_support_validity;

  const onCreateVendor = async () => {
    const updatedVendors = await VendorServices.getVendors();
    setVendors(updatedVendors || []);
    setShowAddVendonDialog(false);
    if (extractedVendorName && updatedVendors && updatedVendors.length > 0) {
      const norm = extractedVendorName.toLowerCase().trim();
      const newlyCreated = updatedVendors.find((v) => (v.name || '').toLowerCase().trim() === norm);
      if (newlyCreated) {
        setVendor(newlyCreated);
      }
      setExtractedVendorName('');
      setExtractedVendorMobile('');
    }
  };

  const openCreateVendorDialog = () => {
    setShowAddVendonDialog(true);
  };
  const closeCreateVendorDialog = () => {
    setShowAddVendonDialog(false);
    setExtractedVendorName('');
    setExtractedVendorMobile('');
  };

  const openCreateItemDialog = (nameOrBarcodeToPrefill = '', explicitBarcode = '') => {
    const trimmed = (nameOrBarcodeToPrefill || '').trim();
    const barcodeTrimmed = (explicitBarcode || '').trim();

    if (barcodeTrimmed) {
      setNewItemBarcode(barcodeTrimmed);
      setNewItemName(trimmed);
    } else if (trimmed) {
      const isDigitsOnly = /^\d{3,}$/.test(trimmed);
      const isAlphanumericCode = /^[A-Za-z0-9_-]{4,}$/.test(trimmed) && /\d/.test(trimmed);
      if (isDigitsOnly || isAlphanumericCode) {
        setNewItemBarcode(trimmed);
        setNewItemName('');
      } else {
        setNewItemName(trimmed);
        setNewItemBarcode('');
      }
    } else {
      setNewItemName('');
      setNewItemBarcode('');
    }
    setShowAddItemDialog(true);
  };

  const closeCreateItemDialog = () => {
    setShowAddItemDialog(false);
    setNewItemName('');
    setNewItemBarcode('');
  };

  const handleItemCreated = (newItem) => {
    if (newItem) {
      setItem(newItem);
      setItemName(newItem.name || '');
      setItemPlu(newItem.plu ? String(newItem.plu) : '');
      setCost(newItem.cost || '');
      const margin = newItem?.config?.profit_margin || '';
      setProfitMargin(margin);
      const costVal = parseFloat(newItem.cost || 0);
      const marginVal = parseFloat(margin);
      if (!isNaN(costVal) && !isNaN(marginVal) && marginVal > 0) {
        const calculatedPrice = calculatePriceFromMargin(costVal, marginVal);
        setPrice(calculatedPrice !== null ? ShopsHelper.getAmountFormatted(shop, calculatedPrice) : newItem.price || '');
      } else {
        setPrice(newItem.price || '');
      }
      setStock(newItem.stock || 0);
      setPrevStock(newItem.stock || 0);
      setTotalAmount((newItem.cost || 0) * quantity);
      if (!newItem.cost || Number(newItem.cost) <= 0) {
        if (costInputRef.current) costInputRef.current.focus();
      } else {
        if (qtyInputRef.current) qtyInputRef.current.focus();
      }
    }
    setShowAddItemDialog(false);
    setNewItemName('');
    setNewItemBarcode('');
  };
  const getPaymentModes = async () => {
    const resp = await AccountServices.getAccountHeads();
    setPaymentModes((resp || []).map((mode) => ({ mode_id: mode.id, name: mode.name, amount: 0 })));
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingIndicator(true);
      try {
        await Promise.all([getVendors(), getWarehouses(), getPaymentModes(), getProducts()]);
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
      }
      setLoadingIndicator(false);
    };
    fetchInitialData();
  }, [getVendors, getWarehouses, getProducts]);

  useEffect(() => {
    if (!featureIsExpired(shop?.warehouse_validity) && shop?.config?.is_warehouse_enabled) {
      if (warehouses?.length > 0 && !editData?.id && warehouse === '') {
        if (shop?.config?.default_warehouse) {
          const defaultWh = warehouses.find((w) => w.id === shop.config.default_warehouse);
          if (defaultWh) {
            setWarehouse(defaultWh.id);
          } else {
            setWarehouse(warehouses[0].id);
          }
        } else {
          setWarehouse(warehouses[0].id);
        }
      }
    }
  }, [warehouses, shop, editData, warehouse]);

  useEffect(() => {
    if (!id) {
      setIsEditLoading(false);
      return;
    }

    const getPurchaseData = async () => {
      setIsEditLoading(true);
      try {
        const purchase = await PurchaseService.getPurchase(id);
        setEditData(purchase);
        const paymentDate =
          purchase.payment_date && purchase.payment_date !== '' ? purchase.payment_date : purchase.bill_date;
        setInvoiceNumber(purchase.invoice_no);
        setBillDate(DateTimeHelpers.convertDateToMDY(purchase.bill_date));
        setPaymentDate(DateTimeHelpers.convertDateToMDY(paymentDate));

        const pDate = purchase.date || purchase.purchase_date;
        if (pDate && typeof pDate === 'string' && pDate.includes('/')) {
          const [dStr, tStr] = pDate.split(' ');
          const [d, m, y] = dStr.split('/');
          if (tStr) {
            const [hh, mm, ss] = tStr.split(':');
            setPurchaseDate(new Date(y, m - 1, d, hh, mm, ss || 0));
          } else {
            setPurchaseDate(new Date(y, m - 1, d));
          }
        } else {
          setPurchaseDate(new Date(pDate));
        }
        const processedItems = (purchase.items || []).map((purchaseItem) => {
          let updatedItem = purchaseItem?.item;
          const itemId = updatedItem?.id;
          const plu = purchaseItem?.plu;
          if (items && items.length > 0) {
            const inventoryItem = items.find(
              (product) =>
                (itemId && product.id === itemId) ||
                (plu && String(product.plu).toLowerCase().trim() === String(plu).toLowerCase().trim())
            );
            if (inventoryItem) {
              updatedItem = inventoryItem;
            }
          }
          const batchPrice = purchaseItem?.product_batch?.price;
          const isBatchEnabled = updatedItem?.is_batch_enabled
          const itemPrice = batchPrice ?? (updatedItem?.price || 0);
          const marginVal = isValidMargin(updatedItem?.config?.profit_margin)
            ? updatedItem.config.profit_margin
            : isValidMargin(purchaseItem.profit_margin)
            ? purchaseItem.profit_margin
            : '';
          const expDate = purchaseItem?.product_batch?.expiry_date || purchaseItem?.expiry_date || null;
          return {
            ...purchaseItem,
            item: updatedItem,
            is_batch_enabled: isBatchEnabled,
            profit_margin: marginVal,
            price: itemPrice,
            batch_price: batchPrice,
            expiry_date: expDate,
          };
        });
        setPurchasedItems(processedItems);
        setVendorNameOld(purchase.vendor_name_old);
        setVendor(purchase.vendor ?? null);
        if (purchase.warehouse !== undefined && purchase.warehouse !== null) {
          setWarehouse(typeof purchase.warehouse === 'object' ? purchase.warehouse.id : purchase.warehouse);
        } else if (purchase.warehouse_id !== undefined && purchase.warehouse_id !== null) {
          setWarehouse(purchase.warehouse_id);
        }
      } catch (err) {
        console.error('Failed to get purchase data:', err);
      } finally {
        setIsEditLoading(false);
      }
    };
    getPurchaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (items && items.length > 0 && purchasedItems && purchasedItems.length > 0) {
      let hasChanges = false;
      const updated = purchasedItems.map((purchaseItem) => {
        const itemId = purchaseItem?.item?.id;
        const plu = purchaseItem?.plu;
        const inventoryItem = items.find(
          (product) =>
            (itemId && product.id === itemId) ||
            (plu && String(product.plu).toLowerCase().trim() === String(plu).toLowerCase().trim())
        );
        if (!inventoryItem) return purchaseItem;

        const marginVal = isValidMargin(inventoryItem?.config?.profit_margin)
          ? inventoryItem.config.profit_margin
          : isValidMargin(purchaseItem.profit_margin)
          ? purchaseItem.profit_margin
          : '';

        if (purchaseItem.item !== inventoryItem || purchaseItem.profit_margin !== marginVal) {
          hasChanges = true;
          return {
            ...purchaseItem,
            item: inventoryItem,
            profit_margin: marginVal,
          };
        }
        return purchaseItem;
      });
      if (hasChanges) {
        setPurchasedItems(updated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (editData && editData.config && editData.config.payment_modes && paymentModes && paymentModes.length > 0) {
      const hasAnyAmount = paymentModes.some((pm) => Number(pm.amount || 0) > 0);
      if (!hasAnyAmount) {
        const oldModes = editData.config.payment_modes;
        const updatedPaymentModes = paymentModes.map((currentMode) => {
          const match = oldModes.find(
            (oldMode) => String(oldMode.mode_id || oldMode.id) === String(currentMode.mode_id || currentMode.id)
          );
          return match ? { ...currentMode, amount: match.amount } : currentMode;
        });
        const hasChanges = updatedPaymentModes.some((pm, idx) => pm.amount !== paymentModes[idx].amount);
        if (hasChanges) {
          setPaymentModes(updatedPaymentModes);
        }
      }
    }
  }, [editData, paymentModes]);

  useEffect(() => {
    if (!id) {
      setBatchName(ShopsHelper.generateBatchName(purchaseDate, vendor));
    }
  }, [vendor, id, purchaseDate]);

  const resetItemFields = () => {
    isResettingRef.current = true;
    setItemName('');
    setItemPlu('');
    setCost('');
    setPrice('');
    setProfitMargin('');
    setQuantity(1);
    setTax('');
    setTotalAmount('');
    setItem(null);
    setStock('');
    setTaxPercentage('');
    setExpiryDate(null);
    setIsSetBatchPrice(false);
    setEditPurchaseItemId(null);
    setTimeout(() => {
      isResettingRef.current = false;
    }, 100);
  };

  const handleChangePayable = (val) => {
    if (quantity === 0 || item === null) return;

    const numVal = Number(val || 0);
    const numQty = Number(quantity || 1);
    let rawCost = 0;
    let newTax = Number(tax || 0);

    if (taxPercentage !== '' && taxPercentage !== null && !isNaN(Number(taxPercentage))) {
      const taxPercent = Number(taxPercentage);
      const base = numVal / (1 + taxPercent / 100);
      rawCost = base / numQty;
      newTax = numVal - base;
      setTax(newTax);
    } else {
      rawCost = (numVal - newTax) / numQty;
      const base = rawCost * numQty;
      const calculatedTaxPercentage = base > 0 ? (newTax / base) * 100 : 0;
      setTaxPercentage(calculatedTaxPercentage);
    }

    setTotalAmount(val);
    setCost(rawCost);
  };

  const handleChangePrice = (val) => {
    setPrice(val);
  };

  const handleChangeRate = (val) => {
    if (quantity === 0 || item === null) return;

    const numVal = Number(val || 0);
    const numQty = Number(quantity || 0);
    const base = numVal * numQty;
    let newTax = Number(tax || 0);

    if (taxPercentage !== '' && taxPercentage !== null && !isNaN(Number(taxPercentage))) {
      newTax = (Number(taxPercentage) * base) / 100;
      setTax(newTax);
    }
    const rawTotal = base + newTax;

    setCost(val);
    setTotalAmount(rawTotal);
  };

  const handleChangeQty = (val) => {
    if (item === null) return;
    setQuantity(val);
    setStock(prevStock + Number(val));

    const qty = Number(val) || 0;
    const base = qty * Number(cost || 0);
    let newTax = Number(tax || 0);

    if (taxPercentage !== '' && taxPercentage !== null && !isNaN(Number(taxPercentage))) {
      newTax = (Number(taxPercentage) * base) / 100;
      setTax(newTax);
    }
    const rawTotal = base + newTax;

    setTotalAmount(rawTotal);
  };

  const handleChangeTaxAmount = (val) => {
    if (item === null) return;
    const amount = Number(val) || 0;
    const base = Number(cost || 0) * Number(quantity || 0);

    const calculatedTaxPercentage = base > 0 ? (amount / base) * 100 : 0;

    setTax(val);
    setTaxPercentage(calculatedTaxPercentage);
    setTotalAmount(base + amount);
  };

  const handleChangeTaxPercentage = (val) => {
    if (item === null) return;
    const percent = Number(val) || 0;
    const base = Number(cost || 0) * Number(quantity || 0);

    const calculatedTax = (percent * base) / 100;

    setTaxPercentage(val);
    setTax(calculatedTax);
    setTotalAmount(base + calculatedTax);
  };

  const addItem = () => {
    let effectiveItem = item;
    if (!effectiveItem && (itemPlu || itemName) && items && items.length > 0) {
      const pNorm = itemPlu ? String(itemPlu).toLowerCase().trim() : '';
      const nNorm = itemName ? itemName.toLowerCase().trim() : '';
      effectiveItem = items.find(
        (p) =>
          (pNorm && String(p.plu).toLowerCase().trim() === pNorm) ||
          (nNorm && (p.name || '').toLowerCase().trim() === nNorm) ||
          (pNorm && p.ean && String(p.ean).toLowerCase().trim() === pNorm)
      );
    }
    if (!effectiveItem && (itemPlu || itemName)) {
      effectiveItem = {
        name: itemName || itemPlu,
        plu: itemPlu || '',
      };
    }
    if (!effectiveItem) return;

    const isBatchEnabled = Boolean(
      effectiveItem?.config?.is_batch_enabled ??
      effectiveItem?.is_batch_enabled ??
      item?.config?.is_batch_enabled ??
      item?.is_batch_enabled
    );

    let finalMargin =
      profitMargin !== '' && profitMargin !== null && !isNaN(Number(profitMargin))
        ? Number(profitMargin)
        : Number(effectiveItem?.config?.profit_margin) || 0;

    let finalBatchPrice = null;
    if (isBatchEnabled && isSetBatchPrice) {
      finalBatchPrice = price !== null && price !== '' && !isNaN(Number(price)) ? Number(price) : null;
    }

    const formattedExp = isBatchEnabled && expiryDate
      ? expiryDate instanceof Date && !isNaN(expiryDate.getTime())
        ? DateTimeHelpers.getTimezoneOffsetDate(expiryDate).toISOString()
        : expiryDate
      : null;

    const effectiveItemConfig = { ...(effectiveItem.config || {}) };
    if (!showProfitMarginAndPrice) {
      delete effectiveItemConfig.profit_margin;
    } else {
      effectiveItemConfig.profit_margin = finalMargin;
    }

    const itemData = {
      ...effectiveItem,
      is_batch_enabled: isBatchEnabled,
      config: effectiveItemConfig,
    };
    if (!showProfitMarginAndPrice) {
      delete itemData.price;
    } else {
      itemData.price = Number(price);
    }

    let data = {
      plu: effectiveItem.plu,
      is_batch_enabled: isBatchEnabled,
      item: itemData,
      rate: Number(cost),
      quantity: Number(quantity),
      tax: Number(tax),
      payable: totalAmount || getLineTotal({ rate: cost, quantity, tax }),
      stock: Number(stock),
      taxPercentage: Number(taxPercentage),
      expiry_date: formattedExp,
    };
    if (showProfitMarginAndPrice) {
      data.price = Number(price);
      data.batch_price = finalBatchPrice;
      data.profit_margin = finalMargin;
    }
    if (editPurchaseItemId) {
      data.id = editPurchaseItemId;
    }
    if (editPurchaseItemId === null) {
      data = {
        ...data,
        purchase: editData.id,
        new_item: true,
      };
    } else {
      const updatingItem = (editData?.items || []).find((itm) => itm.id === editPurchaseItemId);
      data = {
        ...updatingItem,
        ...data,
      };
    }
    setPurchasedItems([...purchasedItems, data]);
    resetItemFields();
    if (pluInputRef.current) {
      pluInputRef.current.focus();
    } else if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  };

  const updateLocalInventory = useCallback(
    (updatedPurchaseItems) => {
      if (!updatedPurchaseItems) return;

      setProducts((prevProducts) => {
        if (!prevProducts) return prevProducts;

        const updatedItemsMap = new Map();
        updatedPurchaseItems.forEach((pi) => {
          if (pi.item && pi.item.id) {
            updatedItemsMap.set(pi.item.id, pi.item);
          }
        });

        let hasChanges = false;
        const newProducts = prevProducts.map((p) => {
          const updatedItem = updatedItemsMap.get(p.id);
          if (updatedItem) {
            hasChanges = true;
            return { ...p, ...updatedItem };
          }
          return p;
        });

        return hasChanges ? newProducts : prevProducts;
      });
    },
    [setProducts]
  );

  const selectItemHandler = useCallback(
    (newItem) => {
      if (!newItem) {
        setItem(null);
        setItemName('');
        setItemPlu('');
        setCost('');
        setProfitMargin('');
        setPrice('');
        setStock(0);
        setPrevStock(0);
        setQuantity(1);
        setTotalAmount(0);
        return;
      }
      setItem(newItem);
      setItemName(newItem.name || '');
      setItemPlu(newItem.plu ? String(newItem.plu) : '');
      setCost(newItem.cost || '');
      const margin = newItem?.config?.profit_margin || '';
      setProfitMargin(margin);
      const costVal = parseFloat(newItem?.cost || 0);
      const marginVal = parseFloat(margin);
      if (!isNaN(costVal) && !isNaN(marginVal) && marginVal > 0) {
        const calculatedPrice = costVal * (1 + marginVal / 100);
        setPrice(ShopsHelper.getAmountFormatted(shop, calculatedPrice));
      } else {
        setPrice(newItem?.price || '');
      }
      setStock(newItem?.stock || 0);
      setPrevStock(newItem?.stock || 0);
      setTotalAmount((newItem?.cost || 0) * quantity);
      setIsSetBatchPrice(false);
      const autoExp = calculateExpiryDate(newItem);
      setExpiryDate(autoExp);
    },
    [quantity, shop, calculateExpiryDate]
  );



  const handleKeyDown = (e, nextRef, prevRef) => {
    if (e.key === 'Tab') {
      if (paymentInputRef.current) {
        e.preventDefault();
        paymentInputRef.current.focus();
      }
    } else if (e.key === 'Enter') {
      const hasEnteredItem = Boolean(item || itemPlu || itemName);
      if (hasEnteredItem) {
        e.preventDefault();
        addItem();
      } else if (nextRef && nextRef.current) {
        e.preventDefault();
        nextRef.current.focus();
      }
    } else if (e.key === 'ArrowRight' || (e.key === 'ArrowDown' && e.target === invoiceInputRef.current)) {
      if (nextRef && nextRef.current) {
        e.preventDefault();
        e.stopPropagation();
        nextRef.current.focus();
      }
    } else if (e.key === 'ArrowLeft') {
      if (prevRef && prevRef.current) {
        e.preventDefault();
        e.stopPropagation();
        prevRef.current.focus();
      }
    }
  };

  const prepareItemsPayload = (itemsList) => {
    return (itemsList || []).map((pi) => {
      const itemObj = typeof pi.item === 'object' && pi.item !== null ? pi.item : null;
      const itemId = itemObj?.id || (typeof pi.item === 'number' ? pi.item : pi.item_id || null);
      const itemName = itemObj?.name || pi.item_name || '';
      const pluVal = pi.plu && !isNaN(Number(pi.plu)) ? Number(pi.plu) : null;
      let expDate = pi.expiry_date || pi.product_batch?.expiry_date || null;
      if (expDate instanceof Date && !isNaN(expDate.getTime())) {
        expDate = DateTimeHelpers.getTimezoneOffsetDate(expDate).toISOString();
      }

      let formattedItem = itemObj ? { ...itemObj } : itemId ? { id: itemId, name: itemName } : null;
      if (!showProfitMarginAndPrice && formattedItem) {
        delete formattedItem.price;
        if (formattedItem.config) {
          const { profit_margin, ...restConfig } = formattedItem.config;
          formattedItem.config = restConfig;
        }
      }

      const payloadItem = {
        ...pi,
        plu: pluVal,
        item_id: itemId,
        item: formattedItem,
        item_name: itemName,
        rate: Number(pi.rate || 0),
        quantity: Number(pi.quantity || 1),
        tax: Number(pi.tax || 0),
        expiry_date: expDate,
      };

      if (showProfitMarginAndPrice) {
        const costVal = Number(pi.rate) || 0;
        const priceVal = getItemSellingPrice(pi);
        const marginVal = costVal > 0 ? Math.round(((priceVal - costVal) / costVal) * 100 * 100) / 100 : 0;
        payloadItem.price = priceVal;
        payloadItem.batch_price = pi.batch_price;
        payloadItem.profit_margin = marginVal;
      } else {
        delete payloadItem.price;
        delete payloadItem.batch_price;
        delete payloadItem.profit_margin;
      }

      return payloadItem;
    });
  };

  const handleItemSearchKeyDown = (e, inputValue, isPluField = false) => {
    if (e.defaultPrevented) return;
    if (e.key === 'Enter') {
      const trimmedInput = (inputValue || '').trim();
      const isCurrentItemMatch = BarcodeHelper.isItemMatch(item, trimmedInput);

      if (item && isCurrentItemMatch && cost && Number(cost) > 0) {
        addItem();
        return;
      }

      if (trimmedInput) {
        const matched = BarcodeHelper.findMatchingItem(trimmedInput, filteredItems);
        if (matched) {
          e.preventDefault();
          selectItemHandler(matched);
          if (!matched.cost || Number(matched.cost) <= 0) {
            if (costInputRef.current) costInputRef.current.focus();
          } else {
            if (qtyInputRef.current) qtyInputRef.current.focus();
          }
          return;
        } else {
          e.preventDefault();
          postAlertMessage({
            text: 'Item not found, create item',
            type: 'warning',
          });
          if (isPluField) {
            openCreateItemDialog('', trimmedInput);
          } else {
            openCreateItemDialog(trimmedInput);
          }
          return;
        }
      }

      if (isPluField) {
        handleKeyDown(e, nameInputRef, invoiceInputRef);
      } else {
        handleKeyDown(e, costInputRef, pluInputRef);
      }
    } else {
      if (isPluField) {
        handleKeyDown(e, nameInputRef, invoiceInputRef);
      } else {
        handleKeyDown(e, costInputRef, pluInputRef);
      }
    }
  };

  useBarcodeScanner({
    onScan: (scannedCode) => {
      if (isShowAddItemDialog || isShowAddVendonDialog || activeItemForDialog || newItemsQueue.length > 0) {
        return;
      }
      const cleanCode = String(scannedCode).trim().toLowerCase();
      if (!cleanCode) return;

      // 1. Check if the scanned barcode matches the currently selected item in the input row
      const isCurrentItemMatch = BarcodeHelper.isItemMatch(item, cleanCode);

      if (isCurrentItemMatch) {
        const currentQty = Number(quantity || 0);
        const newQty = currentQty > 0 ? currentQty + 1 : 2;
        setQuantity(newQty);
        const costVal = parseFloat(cost || item.cost || 0);
        if (!isNaN(costVal)) {
          setTotalAmount(costVal * newQty);
        }
        if (!costVal || costVal <= 0) {
          if (costInputRef.current) costInputRef.current.focus();
        } else {
          if (qtyInputRef.current) qtyInputRef.current.focus();
        }
        return;
      }

      // 2. Search in filteredItems
      const matched = BarcodeHelper.findMatchingItem(scannedCode, filteredItems);

      // 3. Handle match or not found
      if (matched) {
        if (item && item.id && matched.id && item.id === matched.id) {
          const currentQty = Number(quantity || 0);
          const newQty = currentQty > 0 ? currentQty + 1 : 2;
          setQuantity(newQty);
          const costVal = parseFloat(cost || matched.cost || 0);
          if (!isNaN(costVal)) {
            setTotalAmount(costVal * newQty);
          }
          if (!costVal || costVal <= 0) {
            if (costInputRef.current) costInputRef.current.focus();
          } else {
            if (qtyInputRef.current) qtyInputRef.current.focus();
          }
        } else {
          selectItemHandler(matched);
          setQuantity(1);
          if (!matched.cost || Number(matched.cost) <= 0) {
            if (costInputRef.current) costInputRef.current.focus();
          } else {
            if (qtyInputRef.current) qtyInputRef.current.focus();
          }
        }
      } else {
        setQuantity(1);
        postAlertMessage({
          text: 'Item not found, create item',
          type: 'warning',
        });
        setItemName('');
        if (nameInputRef.current) {
          nameInputRef.current.blur();
        }
        openCreateItemDialog('', scannedCode);
      }
    },
    isCapture: true,
    deps: [
      filteredItems,
      selectItemHandler,
      postAlertMessage,
      isShowAddItemDialog,
      isShowAddVendonDialog,
      activeItemForDialog,
      newItemsQueue.length,
      item,
      itemName,
      cost,
      quantity,
    ],
  });
  const handleAddPurchase = async () => {
    setLoadingIndicator(true);
    try {
      const data = {
        vendor_id: vendor?.id,
        warehouse_id: warehouse || null,
        invoice_no: invoiceNumber,
        bill_date: DateTimeHelpers.convertDateToDMY(billDate),
        payment_date: DateTimeHelpers.convertDateToDMY(paymentDate),
        date: DateTimeHelpers.getTimezoneOffsetDate(purchaseDate).toISOString(),
        items: prepareItemsPayload(purchasedItems),

        payment_modes: (paymentModes || []).map((pm) => ({ ...pm, amount: Number(pm.amount || 0) })),
        paid_amount: (paymentModes || []).reduce((sum, pm) => sum + Number(pm.amount || 0), 0),
        vendor_name_old: vendorNameOld,
        batch_name: batchName,
      };
      const resp = await PurchaseService.addPurchase(data);
      if (resp && resp.items) {
        updateLocalInventory(resp.items);
      }
      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchEnabledItems = purchasedItems.some((pi) => {
        const itemObj = pi.item;
        if (itemObj?.is_batch_enabled) return true;
        const subItems = itemObj?.details?.sub_items || [];
        return subItems.some((si) => {
          const subItemObj = (items || []).find((p) => p.id === (si.item_id || si.id));
          return subItemObj?.is_batch_enabled;
        });
      });
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);
      const promises = [];
      promises.push(getProducts({ useCache: false }));

      if (hasBatchEnabledItems && isPremium) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }

      await Promise.all(promises);

      postAlertMessage({
        text: tOperations('Purchase Added successfully'),
        type: 'success',
      });
      history.push(ROUTES.PURCHASE_HISTORY);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const dltItem = (item) => {
    if (item.hasOwnProperty('id')) {
      setRemovedItems((old) => [...old, item.id]);
    }
    setPurchasedItems(purchasedItems.filter((itm) => itm !== item));
  };

  const handleEditItem = (purchaseItem) => {
    isResettingRef.current = true;
    setEditPurchaseItemId(purchaseItem?.id || null);
    const itemId = purchaseItem?.item?.id;
    const plu = purchaseItem?.plu;
    const matchedInvItem = (items || []).find(
      (product) =>
        (itemId && product.id === itemId) ||
        (plu && String(product.plu).toLowerCase().trim() === String(plu).toLowerCase().trim())
    );
    const isBatchEnabled = Boolean(
      matchedInvItem?.is_batch_enabled ??
      purchaseItem?.item?.is_batch_enabled ??
      purchaseItem?.is_batch_enabled
    );
    const effectiveItem = {
      ...(matchedInvItem || purchaseItem.item || {}),
      is_batch_enabled: isBatchEnabled,
    };

    setItem(effectiveItem);
    setItemName(effectiveItem?.name || purchaseItem.item?.name || '');
    setItemPlu(purchaseItem?.plu ? String(purchaseItem.plu) : (effectiveItem?.plu ? String(effectiveItem.plu) : ''));
    setQuantity(purchaseItem?.quantity);
    setCost(purchaseItem?.rate);
    setPrice(getItemSellingPrice(purchaseItem));
    const hasBatchPrice =
      purchaseItem?.batch_price !== null &&
      purchaseItem?.batch_price !== undefined &&
      purchaseItem?.batch_price !== '';
    setIsSetBatchPrice(Boolean(isBatchEnabled && hasBatchPrice));
    setProfitMargin(
      isValidMargin(purchaseItem?.profit_margin)
        ? purchaseItem.profit_margin
        : isValidMargin(effectiveItem?.config?.profit_margin)
        ? effectiveItem.config.profit_margin
        : ''
    );
    setTax(purchaseItem?.tax);
    setTotalAmount(getLineTotal(purchaseItem));
    const calculatedTaxPercentage =
      purchaseItem.taxPercentage ??
      (Number(purchaseItem.rate) > 0
        ? (Number(purchaseItem.tax) / (Number(purchaseItem.rate) * Number(purchaseItem.quantity))) * 100
        : 0);
    setTaxPercentage(calculatedTaxPercentage);
    const exp =
      purchaseItem?.expiry_date
        ? new Date(purchaseItem.expiry_date)
        : purchaseItem?.product_batch?.expiry_date
        ? new Date(purchaseItem.product_batch.expiry_date)
        : calculateExpiryDate(effectiveItem);
    setExpiryDate(exp && !isNaN(exp.getTime()) ? exp : null);
    setPurchasedItems(purchasedItems.filter((itm) => itm !== purchaseItem));
    setStock(editData?.id ? effectiveItem?.stock || 0 : (effectiveItem?.stock || 0) + Number(purchaseItem?.quantity));
    setPrevStock(
      editData?.id ? (effectiveItem?.stock || 0) - Number(purchaseItem?.quantity) : effectiveItem?.stock || 0
    );
    setTimeout(() => {
      isResettingRef.current = false;
    }, 150);
  };

  const handleEditPurchase = async (forceUpdate = false, dataOverride = null) => {
    setLoadingIndicator(true);
    try {
      let data = dataOverride;
      if (!data) {
        const _items = prepareItemsPayload(purchasedItems);
        const olditems = _items.filter((purchaseItem) => !purchaseItem.new_item);
        const newItems = _items.filter((purchaseItem) => purchaseItem.new_item);
        data = {
          id: editData.id,
          vendor_id: vendor?.id,
          warehouse_id: warehouse || null,
          invoice_no: invoiceNumber,
          bill_date: DateTimeHelpers.convertDateToDMY(billDate),
          payment_date: DateTimeHelpers.convertDateToDMY(paymentDate),
          date: DateTimeHelpers.getTimezoneOffsetDate(purchaseDate).toISOString(),
          items: olditems,
          new_items: newItems,
          remove_items: removedItems,
          vendor_name_old: vendorNameOld,
          payment_modes: (paymentModes || []).map((pm) => ({
            mode_id: pm.mode_id,
            name: pm.name,
            amount: Number(pm.amount || 0),
          })),
          paid_amount: (paymentModes || []).reduce((sum, pm) => sum + Number(pm.amount || 0), 0),
          batch_name: batchName,
        };
      }

      if (forceUpdate) {
        data.force_update = true;
      }

      const resp = await PurchaseService.updatePurchase(data, id);
      if (resp && resp.items) {
        updateLocalInventory(resp.items);
      }

      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchInPurchased = purchasedItems.some((pi) => pi.item?.is_batch_enabled);
      const hasBatchInRemoved =
        Array.isArray(removedItems) &&
        removedItems.some((removedId) => {
          const origItem = (editData?.items || []).find((pi) => pi.id === removedId);
          return origItem?.item?.is_batch_enabled;
        });
      const hasBatchEnabled = hasBatchInPurchased || hasBatchInRemoved;
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);

      const promises = [getProducts({ useCache: false })];
      if (isPremium && hasBatchEnabled) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }
      await Promise.all(promises);

      postAlertMessage({
        text: tOperations('Purchase updated successfully'),
        type: 'success',
      });
      setLoadingIndicator(false);
      history.push(ROUTES.PURCHASE_HISTORY);
    } catch (err) {
      setLoadingIndicator(false);
      if (err.status_code === ERROR_CODES.BATCH_RECORDS_EXIST) {
        const _items = prepareItemsPayload(purchasedItems);
        const olditems = _items.filter((purchaseItem) => !purchaseItem.new_item);
        const newItems = _items.filter((purchaseItem) => purchaseItem.new_item);
        const currentData = {
          id: editData.id,
          vendor_id: vendor?.id,
          warehouse_id: warehouse || null,
          invoice_no: invoiceNumber,
          bill_date: DateTimeHelpers.convertDateToDMY(billDate),
          payment_date: DateTimeHelpers.convertDateToDMY(paymentDate),
          date: DateTimeHelpers.getTimezoneOffsetDate(purchaseDate).toISOString(),
          items: olditems,
          new_items: newItems,
          remove_items: removedItems,
          vendor_name_old: vendorNameOld,
          payment_modes: (paymentModes || []).map((pm) => ({
            mode_id: pm.mode_id,
            name: pm.name,
            amount: Number(pm.amount || 0),
          })),
          paid_amount: (paymentModes || []).reduce((sum, pm) => sum + Number(pm.amount || 0), 0),
          batch_name: batchName,
        };
        setPendingUpdateData(currentData);
        setShowForceUpdateDialog(true);
      } else {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
    }
  };

  const handleConfirmForceUpdate = async () => {
    setShowForceUpdateDialog(false);
    if (pendingUpdateData) {
      await handleEditPurchase(true, pendingUpdateData);
    }
  };

  const onSavePurchase = () => {
    if (editData.id) {
      handleEditPurchase();
    } else {
      handleAddPurchase();
    }
  };

  const _getPurchaseSummary = (items) => {
    let totalPayable = 0;
    let totalTax = 0;
    let totalQty = 0;
    items.forEach((item) => {
      const linePayable = getLineTotal(item);
      totalPayable += Number(linePayable) || 0;
      totalTax += Number(item.tax) || 0;
      totalQty += Number(item.quantity) || 0;
    });

    return {
      totalPayable: ShopsHelper.getAmountFormatted(shop, totalPayable),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalQty: roundOff(totalQty, 3),
    };
  };

  const summary = _getPurchaseSummary(purchasedItems);

  const filteredPurchasedItems = purchasedItems.filter((purchasedItem) => {
    if (!searchText) return true;

    const search = searchText.toLowerCase();

    return (
      String(purchasedItem.plu).toLowerCase().includes(search) ||
      purchasedItem.item?.name?.toLowerCase().includes(search) ||
      String(purchasedItem.item?.ean).toLowerCase().includes(search)
    );
  });

  const color = vendor ? (vendor.balance < 0 ? 'red' : vendor.balance > 0 ? 'green' : 'inherit') : 'inherit';
  const dltItemRef = useRef(dltItem);
  dltItemRef.current = dltItem;
  const handleEditItemRef = useRef(handleEditItem);
  handleEditItemRef.current = handleEditItem;

  const columns = useMemo(
    () => [
      { id: 'plu', label: tCommon('PLU'), style: { width: '80px', minWidth: '70px' }, align: 'center', type: 'text' },
      {
        id: 'name',
        label: tCommon('Item Name'),
        style: { minWidth: '200px' },
        align: 'left',
        type: 'callback',
        viewRender: (purchasedItem) => {
          const hasDiscount = Number(purchasedItem.discount) > 0;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{purchasedItem.item?.name}</span>
                {hasDiscount && (
                  <span
                    style={{
                      backgroundColor: '#fef3c7',
                      color: '#b45309',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '12px',
                      border: '1px solid #fde68a',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {purchasedItem.discount}% Disc
                  </span>
                )}
              </div>
              {showProfitMarginAndPrice &&
                purchasedItem.batch_price !== undefined &&
                purchasedItem.batch_price !== null &&
                (purchasedItem.item?.is_batch_enabled || purchasedItem.is_batch_enabled) && (
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#059669',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {tOperations('Batch Price')}:{' '}
                    {ShopsHelper.getAmountFormatted(shop, purchasedItem.batch_price)}
                  </span>
                )}
            </div>
          );
        },
      },
      {
        id: 'cost',
        label: tCommon('Cost'),
        style: { width: '100px', minWidth: '85px' },
        align: 'center',
        type: 'callback',
        viewRender: (purchasedItem) => (
          <div>
            {ShopsHelper.getAmountFormatted(shop, purchasedItem.rate)}
            {purchasedItem.listRate && purchasedItem.listRate > purchasedItem.rate && (
              <div
                style={{
                  textDecoration: 'line-through',
                  color: '#9ca3af',
                  fontSize: '11px',
                }}
              >
                {ShopsHelper.getAmountFormatted(shop, purchasedItem.listRate)}
              </div>
            )}
          </div>
        ),
      },
      ...(showProfitMarginAndPrice
        ? [
            {
              id: 'price',
              label: tCommon('Unit Price'),
              style: { width: '100px', minWidth: '85px' },
              align: 'center',
              type: 'callback',
              viewRender: (purchasedItem) =>
                ShopsHelper.getAmountFormatted(shop, purchasedItem.price ?? purchasedItem.item?.price),
            },
          ]
        : []),
      {
        id: 'quantity',
        label: tCommon('Qty'),
        style: { width: '80px', minWidth: '65px' },
        align: 'center',
        type: 'callback',
        viewRender: (purchasedItem) => (purchasedItem.quantity != null ? roundOff(purchasedItem.quantity, 3) : 1),
      },
      {
        id: 'stock',
        label: tCommon('Stock'),
        style: { width: '80px', minWidth: '65px' },
        align: 'center',
        type: 'callback',
        viewRender: (purchasedItem) =>
          purchasedItem.stock != null
            ? roundOff(purchasedItem.stock, 2)
            : purchasedItem.item?.stock != null
            ? roundOff(purchasedItem.item.stock, 2)
            : 0,
      },
      {
        id: 'tax_percentage',
        label: tCommon('Tax(%)'),
        style: { width: '80px', minWidth: '70px' },
        align: 'center',
        type: 'callback',
        viewRender: (purchasedItem) =>
          purchasedItem.taxPercentage != null
            ? `${roundOff(purchasedItem.taxPercentage, 2)}%`
            : purchasedItem.rate > 0
            ? `${roundOff((purchasedItem.tax / (purchasedItem.rate * purchasedItem.quantity)) * 100, 2)}%`
            : '0%',
      },
      {
        id: 'tax',
        label: tCommon('Tax'),
        style: { width: '90px', minWidth: '80px' },
        align: 'center',
        type: 'callback',
        viewRender: (purchasedItem) => ShopsHelper.getAmountFormatted(shop, purchasedItem.tax),
      },
      {
        id: 'expiry_date',
        label: tOperations('Exp Date'),
        style: { width: '110px', minWidth: '100px' },
        align: 'center',
        type: 'callback',
        viewRender: (purchasedItem) =>
          purchasedItem.expiry_date
            ? DateTimeHelpers.convertDateToDMY(purchasedItem.expiry_date)
            : purchasedItem.product_batch?.expiry_date
            ? DateTimeHelpers.convertDateToDMY(purchasedItem.product_batch.expiry_date)
            : '—',
      },
      {
        id: 'total',
        label: tCommon('Total'),
        style: { width: '110px', minWidth: '95px' },
        align: 'center',
        type: 'callback',
        viewRender: (purchasedItem) => ShopsHelper.getAmountFormatted(shop, getLineTotal(purchasedItem)),
      },
      {
        id: 'action',
        label: tCommon('Action'),
        style: { width: '80px', minWidth: '70px' },
        align: 'center',
        type: 'callback',
        viewRender: (purchasedItem) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton onClick={() => dltItemRef.current(purchasedItem)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => handleEditItemRef.current(purchasedItem)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
        ),
      },
    ],
    [shop, tCommon, tOperations, showProfitMarginAndPrice]
  );
  return (
    <div className={styles.contentWrapper}>
      <div
        className={styles.titleSec}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h2 className={styles.title} style={{ marginBottom: 0 }}>
          {tOperations('Purchase')}
          <span className={styles.menuTitle}>{tOperations('Management')}</span>
        </h2>
        <div>
          <input
            type="file"
            accept="image/*,.pdf,application/pdf"
            id="ai-bill-upload-input"
            style={{ display: 'none' }}
            onChange={handleAiBillUpload}
          />
          <label htmlFor="ai-bill-upload-input">
            <Button
              variant="contained"
              component="span"
              color="primary"
              startIcon={isAiParsing ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
              disabled={isAiParsing}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: '8px',
                padding: '6px 16px',
                textTransform: 'none',
                fontSize: '14px',
                boxShadow: '0 4px 14px 0 rgba(118, 75, 162, 0.39)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                },
              }}
            >
              {isAiParsing ? 'Parsing Invoice (AI)...' : 'Scan Invoice (AI)'}
            </Button>
          </label>
        </div>
      </div>
      <div className={styles.changeable}>
        {aiParseError && (
          <Alert severity="error" onClose={() => setAiParseError(null)} sx={{ mb: 2 }}>
            {aiParseError}
          </Alert>
        )}
        <div
          className={styles.filterSec}
          style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <div className={styles.headTitle} style={{ paddingBottom: 0 }}>
            <h2 className={styles.subTitle} style={{ paddingBottom: 5 }}>
              {tOperations('New Purchase')}
            </h2>
          </div>
        </div>

        <Grid container spacing={3}>
          {/* Column 1: Vendor, Vendor Balance, Invoice Number */}
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                {shop && featureIsExpired(featureValidity) ? (
                  <TextField
                    fullWidth
                    label={tOperations('Vendor')}
                    value={vendorNameOld || ''}
                    variant="outlined"
                    size="small"
                    inputRef={vendorInputRef}
                    onKeyDown={(e) => handleKeyDown(e, invoiceInputRef, null)}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      setVendorNameOld(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e0' },
                        '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                      },
                      '& input': {
                        paddingTop: '10.5px',
                        paddingBottom: '10.5px',
                        fontSize: '14px',
                      },
                    }}
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <TextField
                      select
                      fullWidth
                      label={tOperations('Vendor')}
                      value={vendor?.id || ''}
                      variant="outlined"
                      size="small"
                      inputRef={vendorInputRef}
                      onKeyDown={(e) => handleKeyDown(e, invoiceInputRef, null)}
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected) {
                            return (
                              <em style={{ fontStyle: 'normal', color: '#a0aec0' }}>{tOperations('Select Vendor')}</em>
                            );
                          }
                          const found = vendors.find((v) => v.id === Number(selected));
                          return found ? found.name : '';
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#e2e8f0' },
                          '&:hover fieldset': { borderColor: '#cbd5e0' },
                          '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                        },
                        '& .MuiSelect-select': {
                          paddingTop: '10.5px',
                          paddingBottom: '10.5px',
                          fontSize: '14px',
                        },
                      }}
                      onChange={(e) => {
                        const selectedId = Number(e.target.value);
                        const selectedVendor = vendors.find((v) => v.id === selectedId);
                        setVendor(selectedVendor ?? null);
                        if (selectedVendor) {
                          setTimeout(() => {
                            if (invoiceInputRef.current) {
                              invoiceInputRef.current.focus();
                            }
                          }, 50);
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em style={{ fontStyle: 'normal', color: '#a0aec0' }}>{tOperations('Select Vendor')}</em>
                      </MenuItem>
                      {(vendors || []).map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.name}
                        </MenuItem>
                      ))}
                      <CustomMenuItem onClick={openCreateVendorDialog}>
                        <AddCircleIcon fontSize="small" style={{ marginLeft: 5 }} />
                        <p className={styles.normalText} style={{ fontSize: '12px' }}>
                          {tOperations('New Vendor')}
                        </p>
                      </CustomMenuItem>
                    </TextField>
                    <Tooltip
                      title={extractedVendorName ? `Create Vendor "${extractedVendorName}"` : tOperations('New Vendor')}
                    >
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => setShowAddVendonDialog(true)}
                        style={{ padding: '6px' }}
                      >
                        <AddCircleIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
                {!vendor && extractedVendorName && (
                  <Box
                    mt={1}
                    p={1.5}
                    style={{
                      backgroundColor: '#eff6ff',
                      borderRadius: '8px',
                      border: '1px solid #bfdbfe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}
                  >
                    <Typography variant="body2" style={{ fontSize: '13px', color: '#1e40af', fontWeight: 500 }}>
                      Vendor <strong>"{extractedVendorName}"</strong> detected from bill
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<AddCircleIcon />}
                      onClick={() => setShowAddVendonDialog(true)}
                      style={{ textTransform: 'none', fontSize: '12px', borderRadius: '6px', whiteSpace: 'nowrap' }}
                    >
                      + Create Vendor
                    </Button>
                  </Box>
                )}
              </Grid>
              <Grid item>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={tOperations('Vendor Balance')}
                  InputProps={{ readOnly: true, style: { color } }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  value={
                    loadingIndicator && !vendor
                      ? ''
                      : vendor
                      ? ShopsHelper.getAmountFormatted(shop, vendor.balance)
                      : '—'
                  }
                />
                {loadingIndicator && !vendor && (
                  <Skeleton variant="text" width="60%" animation="wave" style={{ marginTop: 4 }} />
                )}
              </Grid>
              <Grid item>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={tCommon('Invoice Number')}
                  value={invoiceNumber}
                  inputRef={invoiceInputRef}
                  onKeyDown={(e) => handleKeyDown(e, pluInputRef, vendorInputRef)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  onChange={(e) => {
                    setInvoiceNumber(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Column 2: All Dates */}
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    format="dd/MM/yyyy hh:mm a"
                    value={purchaseDate}
                    label={tCommon('Purchase Date')}
                    onChange={(date) => {
                      setPurchaseDate(date);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        margin: 'none',
                        error: false,
                        id: 'date-purchase',
                        InputLabelProps: { shrink: true },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e0' },
                            '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                          },
                          '& input': {
                            paddingTop: '10.5px',
                            paddingBottom: '10.5px',
                            fontSize: '14px',
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    value={billDate}
                    label={tCommon('Bill Date')}
                    onChange={(date) => {
                      setBillDate(date);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        margin: 'none',
                        id: 'date-bill',
                        InputLabelProps: { shrink: true },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e0' },
                            '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                          },
                          '& input': {
                            paddingTop: '10.5px',
                            paddingBottom: '10.5px',
                            fontSize: '14px',
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    value={paymentDate}
                    label={tCommon('Payment Date')}
                    onChange={(date) => {
                      setPaymentDate(date);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        margin: 'none',
                        error: false,
                        id: 'date-payment',
                        InputLabelProps: { shrink: true },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e0' },
                            '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                          },
                          '& input': {
                            paddingTop: '10.5px',
                            paddingBottom: '10.5px',
                            fontSize: '14px',
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 3: Others */}
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={tOperations('Batch Name')}
                  value={batchName}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  onChange={(e) => {
                    setBatchName(e.target.value);
                  }}
                />
              </Grid>
              {!featureIsExpired(shop?.warehouse_validity) && shop?.config?.is_warehouse_enabled && (
                <Grid item>
                  <TextField
                    select
                    fullWidth
                    label={tOperations('Warehouse')}
                    value={warehouse || ''}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e0' },
                        '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                      },
                      '& .MuiSelect-select': {
                        paddingTop: '10.5px',
                        paddingBottom: '10.5px',
                        fontSize: '14px',
                      },
                    }}
                    onChange={(e) => {
                      setWarehouse(e.target.value);
                    }}
                  >
                    {warehouses.map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={tOperations('Search Items')}
                  placeholder={tOperations('Search products')}
                  value={searchText}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon style={{ fontSize: '20px', color: '#718096' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>

      <div className={styles.changeable} style={{ boxShadow: 'none', padding: '0' }}>
        <div className={styles.itemEntryRow}>
          <div className={styles.itemEntryFieldPlu}>
            <Autocomplete
              value={item}
              onChange={(event, newItem) => {
                selectItemHandler(newItem);
              }}
              disableClearable
              inputValue={itemPlu}
              onInputChange={(e, value, reason) => {
                setItemPlu(value);
                if (isResettingRef.current || reason !== 'input' || editPurchaseItemId) return;
                if (value && filteredItems && filteredItems.length > 0) {
                  const matched = filteredItems.find(
                    (p) => String(p.plu).toLowerCase().trim() === value.toLowerCase().trim()
                  );
                  if (matched) {
                    selectItemHandler(matched);
                  }
                }
              }}
              getOptionLabel={(option) => (typeof option === 'object' && option !== null ? String(option.plu) : '')}
              filterOptions={(options, { inputValue }) => {
                const search = (inputValue || '').trim().toLowerCase();
                if (!search) return options;

                const startsWithSearch = [];
                const containsSearch = [];

                (options || []).forEach((option) => {
                  if (!option) return;
                  const pluStr =
                    option.plu !== null && option.plu !== undefined ? String(option.plu).toLowerCase() : '';
                  const eanStr = option.ean && option.ean !== 'nil' ? String(option.ean).toLowerCase() : '';
                  const nameStr = option.name ? option.name.toLowerCase() : '';

                  if (pluStr.startsWith(search)) {
                    startsWithSearch.push(option);
                  } else if (pluStr.includes(search) || eanStr.includes(search) || nameStr.includes(search)) {
                    containsSearch.push(option);
                  }
                });

                return [...startsWithSearch, ...containsSearch];
              }}
              options={filteredItems}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PLU"
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputRef={pluInputRef}
                  inputProps={{
                    ...params.inputProps,
                    onKeyDown: (e) => {
                      if (e.key === 'Tab' || e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                        handleKeyDown(e, nameInputRef, invoiceInputRef);
                      }
                      if (params.inputProps && params.inputProps.onKeyDown) {
                        params.inputProps.onKeyDown(e);
                      }
                    },
                  }}
                  onKeyDown={(e) => handleItemSearchKeyDown(e, itemPlu, true)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>
          <div className={styles.itemEntryFieldName} style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Autocomplete
                style={{ flex: 1 }}
                value={item}
                onChange={(event, newItem) => {
                  if (!newItem) {
                    selectItemHandler(null);
                    return;
                  }
                  if (newItem && newItem.isNewItemOption) {
                    openCreateItemDialog(newItem.inputValue || itemName);
                    return;
                  }
                  selectItemHandler(newItem);
                }}
                inputValue={itemName}
                onInputChange={(event, newInputValue) => {
                  setItemName(newInputValue);
                }}
                options={filteredItems}
                loading={!items}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option?.isNewItemOption) return option.name;
                  return option?.name || '';
                }}
                filterOptions={(options, params) => {
                  const { inputValue } = params;
                  const filtered = BarcodeHelper.filterItemsByBarcode(options, inputValue);
                  const searchTrimmed = (inputValue || '').trim();
                  if (searchTrimmed !== '') {
                    const isExisting = options.some((option) =>
                      BarcodeHelper.isItemMatch(option, searchTrimmed)
                    );
                    if (!isExisting) {
                      filtered.push({
                        isNewItemOption: true,
                        inputValue: searchTrimmed,
                        name: `+ Create "${searchTrimmed}"`,
                      });
                    }
                  }
                  return filtered;
                }}
                renderOption={(props, option) => {
                  if (option.isNewItemOption) {
                    return (
                      <li
                        {...props}
                        key="create-new-item-option"
                        style={{
                          color: '#2563eb',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          padding: '10px 14px',
                          borderTop: '1px solid #e2e8f0',
                          backgroundColor: '#f0f7ff',
                        }}
                      >
                        <AddCircleIcon style={{ fontSize: '18px', color: '#2563eb' }} />
                        <span>Create "{option.inputValue}"</span>
                      </li>
                    );
                  }
                  const eanText = option.ean && option.ean !== 'nil' ? option.ean : null;
                  return (
                    <li {...props} key={option.id || option.plu}>
                      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500, fontSize: '14px' }}>{option.name}</span>
                          <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                            PLU: {option.plu}
                          </span>
                        </div>
                        {eanText && (
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            Barcode: {eanText}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tOperations('Item Search')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={tOperations('Name or Barcode')}
                    inputRef={nameInputRef}
                    inputProps={{
                      ...params.inputProps,
                      onKeyDown: (e) => {
                        if (e.key === 'Tab' || e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                          handleKeyDown(e, costInputRef, pluInputRef);
                        }
                        if (params.inputProps && params.inputProps.onKeyDown) {
                          params.inputProps.onKeyDown(e);
                        }
                      },
                    }}
                    onKeyDown={(e) => handleItemSearchKeyDown(e, itemName, false)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Tooltip title="Barcode Scan Supported">
                            <QrCodeScannerIcon style={{ fontSize: '18px', color: '#2563eb', marginRight: 4 }} />
                          </Tooltip>
                          {itemName.trim() &&
                            !filteredItems.some(
                              (i) => i.name?.toLowerCase() === itemName.trim().toLowerCase()
                            ) && (
                              <Tooltip title={`Create "${itemName.trim()}" as new item`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCreateItemDialog(itemName.trim());
                                  }}
                                  style={{ color: '#2563eb', padding: 2 }}
                                >
                                  <AddCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e0' },
                        '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                      },
                      '& input': {
                        paddingTop: '10.5px',
                        paddingBottom: '10.5px',
                        fontSize: '14px',
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled={editPurchaseItemId}
                  />
                )}
              />
              {!isItemAlreadyInDb && itemName.trim() !== '' && (
                <Tooltip title={tCommon('Create New Item')}>
                  <IconButton
                    onClick={handleOpenDirectCreateItemDialog}
                    color="primary"
                    size="small"
                    sx={{ ml: 0.5, p: '8px' }}
                  >
                    <AddCircleIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            {showProfitMarginAndPrice && item?.is_batch_enabled && (
              <Button
                component="span"
                onClick={() => setIsSetBatchPrice((prev) => !prev)}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '2px',
                  padding: 0,
                  minWidth: 'auto',
                  fontSize: '11px',
                  fontWeight: isSetBatchPrice ? 600 : 500,
                  color: isSetBatchPrice ? '#059669' : '#64748b',
                  textTransform: 'none',
                  textDecoration: isSetBatchPrice ? 'none' : 'underline',
                  lineHeight: 1.2,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  whiteSpace: 'nowrap',
                  zIndex: 2,
                }}
              >
                {isSetBatchPrice && (
                  <CheckRoundedIcon style={{ fontSize: '14px', color: '#059669' }} />
                )}
                <span>{tOperations('Set this price as batch price')}</span>
              </Button>
            )}
          </div>
          <div className={styles.itemEntryField}>
            <NumberField
              label={tCommon('Cost')}
              value={cost}
              onChange={(e) => handleChangeRate(e.target.value)}
              onBlur={(e) => {
                if (isResettingRef.current) return;
                const val = e.target.value;
                setCost(!val || val.trim() === '' ? '' : ShopsHelper.getAmountFormatted(shop, val));
              }}
              inputRef={costInputRef}
              onKeyDown={(e) =>
                handleKeyDown(e, showProfitMarginAndPrice ? priceInputRef : qtyInputRef, nameInputRef)
              }
              InputLabelProps={{ shrink: true }}
              helperText={
                showProfitMarginAndPrice &&
                item &&
                displayedMargin !== null && (
                  <Button
                    component="span"
                    onClick={handleOpenProductMargin}
                    style={{
                      padding: 0,
                      minWidth: 'auto',
                      fontSize: '10px',
                      color: '#1976d2',
                      fontWeight: 600,
                      textTransform: 'none',
                      textDecoration: 'underline',
                      lineHeight: 1,
                      cursor: 'pointer',
                    }}
                  >
                    {`Margin: ${displayedMargin}%`}
                  </Button>
                )
              }
              FormHelperTextProps={{
                style: { position: 'absolute', top: '100%', margin: 0, marginTop: '2px', whiteSpace: 'nowrap' },
              }}
            />
          </div>
          {showProfitMarginAndPrice && (
            <div className={styles.itemEntryField}>
              <NumberField
                label={tCommon('Price')}
                value={price}
                onChange={(e) => handleChangePrice(e.target.value)}
                onBlur={(e) => {
                  if (isResettingRef.current) return;
                  const val = e.target.value;
                  setPrice(!val || val.trim() === '' ? '' : ShopsHelper.getAmountFormatted(shop, val));
                }}
                inputRef={priceInputRef}
                onKeyDown={(e) => handleKeyDown(e, qtyInputRef, costInputRef)}
                InputLabelProps={{ shrink: true }}
                helperText={
                  suggestedPrice !== null && (
                    <Button
                      component="span"
                      onClick={() => {
                        if (suggestedPrice !== null && suggestedPrice !== undefined) {
                          setPrice(suggestedPrice);
                        }
                      }}
                      style={{
                        padding: 0,
                        minWidth: 'auto',
                        fontSize: '10px',
                        color: '#718096',
                        fontWeight: 500,
                        textTransform: 'none',
                        lineHeight: 1,
                        cursor: 'pointer',
                      }}
                    >
                      {`Sug. Price: ${suggestedPrice}`}
                    </Button>
                  )
                }
                FormHelperTextProps={{
                  style: { position: 'absolute', top: '100%', margin: 0, marginTop: '2px', whiteSpace: 'nowrap' },
                }}
              />
            </div>
          )}
          <div className={styles.itemEntryFieldSmall}>
            <NumberField
              label={tCommon('Qty')}
              value={quantity}
              onChange={(e) => handleChangeQty(e.target.value)}
              inputRef={qtyInputRef}
              onKeyDown={(e) =>
                handleKeyDown(e, taxPercentInputRef, showProfitMarginAndPrice ? priceInputRef : costInputRef)
              }
              InputLabelProps={{ shrink: true }}
              helperText={
                item && <span style={{ fontSize: '10px', color: '#718096', fontWeight: 500 }}>Stock: {stock}</span>
              }
              FormHelperTextProps={{
                style: { position: 'absolute', top: '100%', margin: 0, marginTop: '2px', whiteSpace: 'nowrap' },
              }}
            />
          </div>
          <div className={styles.itemEntryFieldSmall}>
            <NumberField
              label={tCommon('Tax(%)')}
              value={taxPercentage}
              onChange={(e) => handleChangeTaxPercentage(e.target.value)}
              onBlur={(e) => {
                if (isResettingRef.current) return;
                const val = e.target.value;
                setTaxPercentage(!val || val.trim() === '' ? '' : ShopsHelper.getAmountFormatted(shop, val));
              }}
              inputRef={taxPercentInputRef}
              onKeyDown={(e) => handleKeyDown(e, taxAmountInputRef, qtyInputRef)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.itemEntryField}>
            <NumberField
              label={tCommon('Tax')}
              value={tax}
              onChange={(e) => handleChangeTaxAmount(e.target.value)}
              onBlur={(e) => {
                if (isResettingRef.current) return;
                const val = e.target.value;
                setTax(!val || val.trim() === '' ? '' : ShopsHelper.getAmountFormatted(shop, val));
              }}
              inputRef={taxAmountInputRef}
              onKeyDown={(e) =>
                handleKeyDown(
                  e,
                  isColumnVisible('expiry_date') && Boolean(item?.config?.is_batch_enabled ?? item?.is_batch_enabled)
                    ? expDateInputRef
                    : totalInputRef,
                  taxPercentInputRef
                )
              }
              InputLabelProps={{ shrink: true }}
            />
          </div>
          {isColumnVisible('expiry_date') && (
            <div className={styles.itemEntryFieldDate}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={!Boolean(item?.config?.is_batch_enabled ?? item?.is_batch_enabled)}
                  format="dd/MM/yyyy"
                  value={Boolean(item?.config?.is_batch_enabled ?? item?.is_batch_enabled) ? expiryDate : null}
                  label={tOperations('Exp Date')}
                  onChange={(newDate) => {
                    setExpiryDate(newDate);
                  }}
                  slotProps={{
                    textField: {
                      disabled: !Boolean(item?.config?.is_batch_enabled ?? item?.is_batch_enabled),
                      size: 'small',
                      fullWidth: true,
                      margin: 'none',
                      id: 'item-exp-date',
                      InputLabelProps: { shrink: true },
                      inputRef: expDateInputRef,
                      onKeyDown: (e) => handleKeyDown(e, totalInputRef, taxAmountInputRef),
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: Boolean(item?.config?.is_batch_enabled ?? item?.is_batch_enabled)
                            ? '#fff'
                            : '#f7fafc',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#e2e8f0' },
                          '&:hover fieldset': { borderColor: '#cbd5e0' },
                          '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                        },
                        '& input': {
                          paddingTop: '10.5px',
                          paddingBottom: '10.5px',
                          fontSize: '14px',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          )}
          <div className={styles.itemEntryFieldTotal}>
            <NumberField
              label={tCommon('Total')}
              value={totalAmount}
              onChange={(e) => handleChangePayable(e.target.value)}
              onBlur={(e) => {
                if (isResettingRef.current) return;
                const val = e.target.value;
                setTotalAmount(!val || val.trim() === '' ? '' : ShopsHelper.getAmountFormatted(shop, val));
              }}
              inputRef={totalInputRef}
              onKeyDown={(e) =>
                handleKeyDown(
                  e,
                  null,
                  isColumnVisible('expiry_date') && Boolean(item?.config?.is_batch_enabled ?? item?.is_batch_enabled)
                    ? expDateInputRef
                    : taxAmountInputRef
                )
              }
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.itemEntryAction}>
            <Tooltip title={tCommon('Add Items')}>
              <span>
                <IconButton disabled={itemPlu === '' || itemName === '' || cost === ''} onClick={addItem} size="small">
                  <LibraryAddIcon color="primary" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={filteredPurchasedItems}
        getRowKey={(row, index) => row.id || index}
        isLoading={isEditLoading}
        columnFilterId={PURCHASE_TABLE_COLUMNS_KEY}
        optionalColumns={optionalColumns}
        tableStyle={{ minWidth: 900 }}
        rowStyle={(row) => (Number(row.discount) > 0 ? { backgroundColor: '#fffbeb' } : {})}
        summary={{
          name: tCommon('Total'),
          quantity: summary.totalQty,
          tax: summary.totalTax,
          total: summary.totalPayable,
        }}
      >
        {filteredPurchasedItems.length === 0 && !isEditLoading && (
          <TableRow>
            <TableCell colSpan={12} align="center">
              {tOperations('No matching items found')}
            </TableCell>
          </TableRow>
        )}
      </DataTable>

      <div className={styles.changeable} style={{ marginTop: 12 }}>
        <h3 className={styles.subTitle} style={{ marginBottom: 16 }}>
          {tOperations('Payment Information')}
        </h3>
        <TableContainer component={Paper}>
          <Table aria-label="customized table">
            <TableHead>
              <TableRow>
                {(paymentModes || []).map((pm, index) => (
                  <TableCell key={pm.mode_id}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                      <NumberField
                        inputRef={index === 0 ? paymentInputRef : undefined}
                        label={pm.name}
                        value={pm.amount || ''}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const payableAmount = Number(summary.totalPayable);
                            setPaymentModes((prev) =>
                              prev.map((item) =>
                                item.mode_id === pm.mode_id
                                  ? { ...item, amount: payableAmount }
                                  : { ...item, amount: 0 }
                              )
                            );
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            onSavePurchase();
                          }
                        }}
                        onChange={(e) => {
                          const updatedAmount = e.target.value;
                          setPaymentModes((prev) =>
                            prev.map((item) =>
                              item.mode_id === pm.mode_id ? { ...item, amount: updatedAmount } : item
                            )
                          );
                        }}
                        onBlur={() => {
                          setPaymentModes((prev) =>
                            prev.map((item) =>
                              item.mode_id === pm.mode_id
                                ? {
                                    ...item,
                                    amount: pm.amount === '' ? '' : ShopsHelper.getAmountFormatted(shop, pm.amount),
                                  }
                                : item
                            )
                          );
                        }}
                      />
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        className={styles.payFullBtn}
                        title={tOperations('Pay Full')}
                        onClick={() => {
                          const payableAmount = Number(summary.totalPayable);
                          setPaymentModes((prev) =>
                            prev.map((item) =>
                              item.mode_id === pm.mode_id ? { ...item, amount: payableAmount } : { ...item, amount: 0 }
                            )
                          );
                        }}
                      >
                        {tOperations('Payment In Full')}
                      </Button>
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      </div>
      <Grid container className={styles.submitSection} justifyContent="flex-end">
        <Link to={ROUTES.PURCHASE_HISTORY} className={styles.link}>
          <Button variant="contained" color="secondary" className={styles.closeBtn}>
            {tCommon('Cancel')}
          </Button>
        </Link>
        <Button
          variant="contained"
          style={{
            backgroundColor: '#00a65a',
            borderRadius: '8px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
          disabled={
            invoiceNumber === '' ||
            billDate === '' ||
            purchaseDate === '' ||
            purchasedItems.length === 0 ||
            loadingIndicator
          }
          onClick={onSavePurchase}
          startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {editData.id ? 'Update Purchase' : 'Save Purchase'}
        </Button>
      </Grid>
      {isShowAddVendonDialog && (
        <NewVendorDialog
          initialName={extractedVendorName}
          initialMobile={extractedVendorMobile}
          onClose={closeCreateVendorDialog}
          onSuccess={onCreateVendor}
        />
      )}
      {isShowAddItemDialog && (
        <AddItemDialog
          handleClose={closeCreateItemDialog}
          onSuccess={handleItemCreated}
          initialName={newItemName}
          initialBarcode={newItemBarcode}
          initialType={ITEM_TYPES.INVENTORY}
        />
      )}

      {newItemsQueue.length > 0 && (
        <NewItemsTableDialog
          open={newItemsQueue.length > 0 && !activeItemForDialog && !isShowAddVendonDialog}
          newItems={newItemsQueue}
          onOpenCreateDialog={handleOpenItemDialogForCreate}
          createdIndices={createdRowIndices}
          onCreateItems={handleCreateNewItems}
          onDone={handleDoneNewItems}
          onSkip={handleSkipNewItems}
          onCancel={handleCancelNewItems}
          postAlertMessage={postAlertMessage}
        />
      )}

      {activeItemForDialog && (
        <ItemDialog
          item={{
            name: activeItemForDialog.rowItem.name,
            cost: activeItemForDialog.rowItem.rate,
            price: activeItemForDialog.rowItem.price,
            stock: activeItemForDialog.rowItem.stock,
            plu: activeItemForDialog.rowItem.plu,
          }}
          itemCount={items?.length || 0}
          handleClose={handleItemDialogClose}
        />
      )}

      {showProfitMarginAndPrice && isShowMarginEditDialog && (
        <MarginEditDialog
          open={isShowMarginEditDialog}
          handleClose={() => setIsShowMarginEditDialog(false)}
          initialMargin={displayedMargin}
          cost={cost}
          price={price}
          itemName={item?.name}
          onSave={(newMargin, newPrice) => {
            setProfitMargin(newMargin);
            if (newPrice !== undefined && newPrice !== null && newPrice !== '') {
              setPrice(newPrice);
            }
          }}
        />
      )}

      <ConfirmationDialog
        open={isShowForceUpdateDialog}
        handleClose={() => setShowForceUpdateDialog(false)}
        handleConfirm={handleConfirmForceUpdate}
        title={tOperations('Confirm Force Update')}
        message={tOperations(
          'Inventory Conflict: Sales already exist for items in this batch. A force update is required to proceed. Do you wish to continue?'
        )}
        confirmText={tCommon('Yes')}
        cancelText={tCommon('Cancel')}
        loading={loadingIndicator}
      />


    </div>
  );
}

export default withConsoleBase(AddPurchase);
